import React, { ReactNode, useLayoutEffect, useRef, useState } from 'react';

import { ChevronDown } from 'lucide-react';

import { OutsideAlerter } from '../utilities/OutsideAlerter';

export interface ISelectOption {
    value: string | null;
    label: string;
};
interface ISelect {
    selected: ISelectOption | null;
    preBtnIcon?: ReactNode;
    postBtnIcon?: ReactNode;
    options: ISelectOption[];
    size?: 'sm' | 'md' | 'lg';
    fluid?: boolean;
    disabled?: boolean;
    onSelect: (value: ISelectOption) => void;
};
export const Select = (props: ISelect) => {
    const { selected, preBtnIcon, postBtnIcon, options, size = 'sm', fluid, disabled, onSelect } = props;

    const mainBtnRef = useRef<HTMLButtonElement>(null);
    const hiddenBtnsRef = useRef<HTMLDivElement>(null);

    const [open, setOpen] = useState<boolean>(false);

    useLayoutEffect(() => {
        if (!hiddenBtnsRef.current || !mainBtnRef.current) return;
        if (fluid) return;

        let maxWidth = 0;

        const buttons = hiddenBtnsRef.current.children;

        for (const btn of Array.from(buttons)) {
            const width = (btn as HTMLElement).getBoundingClientRect().width;
            maxWidth = Math.max(maxWidth, width);
        };

        mainBtnRef.current.style.minWidth = `${maxWidth}px`;

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options, fluid]);

    const handleToggle = () => {
        if (disabled) return;

        setOpen(!open);
    };

    const handleSelect = (option: ISelectOption) => {
        if (disabled) return;

        onSelect(option);
        setOpen(false);
    };

    return (
        <OutsideAlerter callback={() => setOpen(false)} isActive={open}>
            <div className={`select ${fluid ? 'select--fluid' : ''} select--${size} ${open ? 'select--open' : ''}`}>
                <button
                    ref={mainBtnRef}
                    className={`select--main-btn ${disabled ? 'select--main-btn--disabled' : ''}`}
                    onClick={handleToggle}
                >
                    {preBtnIcon}
                    <div className='select--main-btn--right'>
                        <span>{selected?.label ?? '-- Select --'}</span>
                        {postBtnIcon ?? <ChevronDown style={{ rotate: open ? "180deg" : "0deg" }} />}
                    </div>
                </button>

                <div ref={hiddenBtnsRef} className="select--hidden-measure">
                    {options.slice(0, 2).map(option => (
                        <button key={option.value} className='select--main-btn'>
                            {preBtnIcon}
                            <div className='select--main-btn--right'>
                                <span>{option.label}</span>
                                {postBtnIcon ?? <ChevronDown />}
                            </div>
                        </button>
                    ))}
                </div>

                {open &&
                    <div className='select--options'>
                        {options.map(option => (
                            <button
                                key={option.value}
                                onClick={() => handleSelect(option)}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                }
            </div>
        </OutsideAlerter>
    );
};